import React, { useState, useEffect, useCallback, useRef } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Shield, Cloud, Database, Phone, ChevronLeft, ChevronDown, Menu, LogOut, SlidersHorizontal, Clock } from 'lucide-react';
import { LOGO_SRC } from '../data/logo';
import { apiFetch, readApiJson } from '../lib/api';
import { EVENEMENT_MAIA_VU, lireMaiaAttendCompte } from '../lib/maiaApi';
import NotificationBell from './NotificationBell';
import CartBellWidget from './CartBellWidget';
import GlobalSearch from './GlobalSearch';
import EntitySelector from './EntitySelector';
import OrgSelector, { useOrg } from './OrgSelector';
import { useAuth } from '../hooks/useAuth';
import { usePreferences } from '../hooks/usePreferences';

import { flattenMenuResults } from '../lib/menuSearch';
import sidebarCategories, { itemVisibleDansOrg, itemVisiblePourCompte, sectionVisibleDansOrg } from './sidebar/sidebarCategories';
import CollapsedCategory from './sidebar/CollapsedCategory';
export { sidebarCategories };







const reportSubItems = [
  { path: '/reports/eset', label: 'ESET Protect', icon: Shield },
  { path: '/reports/microsoft', label: 'Microsoft 365', icon: Cloud },
  { path: '/reports/acronis', label: 'Acronis Backup', icon: Database },
  { path: '/reports/telephony', label: 'Téléphonie', icon: Phone },
];





export default function Sidebar({ open, onToggle, mobileOpen, onWidthChange }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user, effectiveUser, hasPermission, defaultPath } = useAuth();
  const { activeOrg } = useOrg();
  const {
    sidebarOrder, sidebarItems,
    sidebarAlphaSortSections, sidebarAlphaSortItems,
    customLabels, setSidebarOrder,
    setSidebarAlphaSortSections, setSidebarAlphaSortItems,
  } = usePreferences();
  const isReportPage = location.pathname.startsWith('/reports/');

  // Detect narrow viewport — treat as collapsed regardless of `open` state
  const [isNarrow, setIsNarrow] = React.useState(() => typeof window !== 'undefined' && window.innerWidth <= 1024);
  React.useEffect(() => {
    const mq = window.matchMedia('(max-width: 1024px)');
    const handler = (e) => setIsNarrow(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Effective collapsed state: manual toggle OR narrow viewport
  const collapsed = !open || isNarrow;

  // ── Recent pages ────────────────────────────────────────────────────────
  const [recentPages, setRecentPages] = useState(() => {
    try { return JSON.parse(localStorage.getItem('sidebar_recent_pages') || '[]'); }
    catch { return []; }
  });

  // ── Badge dynamique : Attributs inconnus (compteur des keys 'new') ─────
  const [unknownAttrsCount, setUnknownAttrsCount] = useState(0);
  // Le badge sert l'entrée « Revue catalogue » (catalog.review) ; le poll
  // tournait pour tout le monde, en fetch brut (sans X-Active-Org ni détection
  // de maintenance), vers un endpoint qui exige ticketing.manage → 403 toutes
  // les 60 s pour les autres rôles. Le booléen est calculé hors de l'effet :
  // hasPermission change d'identité à chaque rendu.
  const canSeeUnknownAttrs = hasPermission('catalog.review', activeOrg?.code);
  useEffect(() => {
    if (!canSeeUnknownAttrs) { setUnknownAttrsCount(0); return undefined; }
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const data = await apiFetch('/api/catalog/unknown-attrs/count').then(readApiJson);
        if (!cancelled && data.ok) setUnknownAttrsCount(data.count || 0);
      } catch {}
    };
    fetchCount();
    const interval = setInterval(fetchCount, 60000); // refresh toutes les 60s
    return () => { cancelled = true; clearInterval(interval); };
  }, [canSeeUnknownAttrs]);

  // ── Badge « Maia attend » (#20769) : questions à répondre + demandes en cours ou finies non vues. Internes seulement
  // (le serveur rend 0 aux autres) ; relu chaque minute, et tout de suite quand le lecteur vient de voir un onglet Maia.
  const [maiaCount, setMaiaCount] = useState(0);
  const voitMaia = hasPermission('ticketing.view', activeOrg?.code)
    && itemVisiblePourCompte({ internes: true }, effectiveUser || user, activeOrg?.code);
  useEffect(() => {
    if (!voitMaia) { setMaiaCount(0); return undefined; }
    let cancelled = false;
    const lire = () => lireMaiaAttendCompte().then((d) => { if (!cancelled) setMaiaCount(d.a_voir || 0); }).catch(() => {});
    lire();
    const interval = setInterval(lire, 60000);
    window.addEventListener(EVENEMENT_MAIA_VU, lire);
    return () => { cancelled = true; clearInterval(interval); window.removeEventListener(EVENEMENT_MAIA_VU, lire); };
  }, [voitMaia]);

  // Map badge type → count pour rendu dans les items
  const BADGE_COUNTS = { unknown_attrs: unknownAttrsCount, maia: maiaCount };

  // Resizable sidebar width
  const COLLAPSED_W = 60;
  const DEFAULT_W = 240;
  const MIN_W = 180;
  const MAX_W = 400;
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    try { return parseInt(localStorage.getItem('sidebar_width')) || DEFAULT_W; }
    catch { return DEFAULT_W; }
  });
  const resizing = useRef(false);

  const effectiveWidth = collapsed ? COLLAPSED_W : sidebarWidth;

  // Notify parent of width changes
  useEffect(() => {
    if (onWidthChange) onWidthChange(effectiveWidth);
  }, [effectiveWidth, onWidthChange]);

  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    resizing.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const onMove = (ev) => {
      if (!resizing.current) return;
      const x = ev.clientX || (ev.touches && ev.touches[0].clientX) || 0;
      const clamped = Math.min(MAX_W, Math.max(MIN_W, x));
      setSidebarWidth(clamped);
    };
    const onUp = () => {
      resizing.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onUp);
      // Persist
      try { localStorage.setItem('sidebar_width', String(Math.min(MAX_W, Math.max(MIN_W, parseInt(document.querySelector('[data-testid="sidebar"]')?.style.width) || DEFAULT_W)))); } catch {}
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onUp);
  }, []);

  // Persist width on change (debounced)
  useEffect(() => {
    if (!collapsed) {
      try { localStorage.setItem('sidebar_width', String(sidebarWidth)); } catch {}
    }
  }, [sidebarWidth, collapsed]);

  // Build a lookup of all items across all categories by path
  const allItemsByPath = React.useMemo(() => {
    const map = {};
    sidebarCategories.forEach(cat => {
      cat.items.forEach(item => { map[item.path] = item; });
    });
    return map;
  }, []);

  // Track page visits for "Récents" (doit être APRÈS allItemsByPath)
  useEffect(() => {
    const item = allItemsByPath[location.pathname];
    if (!item) return;
    setRecentPages(prev => {
      const filtered = prev.filter(r => r.path !== location.pathname);
      const next = [{ path: location.pathname, label: item.label, ts: Date.now() }, ...filtered].slice(0, 5);
      try { localStorage.setItem('sidebar_recent_pages', JSON.stringify(next)); } catch {}
      return next;
    });
  }, [location.pathname, allItemsByPath]);

  // Apply persisted order (sections + items) — ou tri alphabetique si flag active
  const orderedCategories = React.useMemo(() => {
    const collator = new Intl.Collator('fr', { sensitivity: 'base', numeric: true });
    // Tri sur le label (custom rename éventuel pris en compte). On utilisait
    // `name || id` historiquement mais les sections ont un `label`, pas un
    // `name` — d'où la chute en bout de liste de "BASE DE CONNAISSANCES"
    // (triée sur l'id "kb").
    const sectionSortKey = (cat) => customLabels[cat.id] || cat.label || cat.id;
    const sorted = sidebarAlphaSortSections
      ? [...sidebarCategories].sort((a, b) => collator.compare(sectionSortKey(a), sectionSortKey(b)))
      : (sidebarOrder
          ? [...sidebarCategories].sort((a, b) => {
              const ia = sidebarOrder.indexOf(a.id);
              const ib = sidebarOrder.indexOf(b.id);
              return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
            })
          : sidebarCategories);

    if (!sidebarItems) return sorted;

    // Build reverse lookup: path → default category id
    const pathToDefaultCat = {};
    sidebarCategories.forEach(cat => {
      cat.items.forEach(item => { pathToDefaultCat[item.path] = cat.id; });
    });

    // Apply custom item assignments per category — allow cross-section moves
    // First, collect all paths claimed by custom assignments to avoid duplicates
    const claimedPaths = new Set();
    sorted.forEach(cat => {
      const customPaths = sidebarItems[cat.id];
      if (customPaths) customPaths.forEach(p => claimedPaths.add(p));
    });

    const result = sorted.map(cat => {
      const customPaths = sidebarItems[cat.id];
      if (!customPaths) {
        // No custom config — use defaults, but exclude items claimed by other sections
        return { ...cat, items: cat.items.filter(item => !claimedPaths.has(item.path)) };
      }
      // Resolve custom paths to items (cross-section allowed), deduplicate
      const seenPaths = new Set();
      const customItems = customPaths
        .map(path => allItemsByPath[path])
        .filter(item => { if (!item || seenPaths.has(item.path)) return false; seenPaths.add(item.path); return true; });
      // Append default items not claimed by any custom config (new items)
      const defaultCat = sidebarCategories.find(c => c.id === cat.id);
      if (defaultCat) {
        defaultCat.items.forEach(item => {
          if (!seenPaths.has(item.path) && !claimedPaths.has(item.path)) {
            seenPaths.add(item.path);
            customItems.push(item);
          }
        });
      }
      return { ...cat, items: customItems };
    });

    // Tri alphabetique des items de chaque section si active
    if (sidebarAlphaSortItems) {
      return result.map(cat => ({
        ...cat,
        items: [...cat.items].sort((a, b) => collator.compare(
          customLabels[a.path] || a.label,
          customLabels[b.path] || b.label,
        )),
      }));
    }
    return result;
  }, [sidebarOrder, sidebarItems, sidebarAlphaSortSections, sidebarAlphaSortItems, customLabels, allItemsByPath]);

  // Drapeau superadmin réutilisé par isCategoryAllowedForOrg et la boucle de rendu
  const isSuperadmin = user?.role === 'superadmin';

  // Helpers de filtrage partagés (DRY) — utilisés par searchMenuItems,
  // activeSection, activeSectionVisibleItems et la boucle principale de rendu.
  const isCategoryAllowedForOrg = React.useCallback(
    (cat) => sectionVisibleDansOrg(cat, { isSuperadmin, activeOrg }),
    [isSuperadmin, activeOrg],
  );

  const filterItemsByRoleAndPerms = React.useCallback((items) => (
    items.filter(item => {
      if (item.role && user?.role !== item.role) return false;
      if (item.hideForRole && user?.role === item.hideForRole) return false;
      if (!itemVisibleDansOrg(item, activeOrg?.code)) return false;
      if (!itemVisiblePourCompte(item, effectiveUser || user, activeOrg?.code)) return false;
      const perms = item.permissions || (item.permission ? [item.permission] : []);
      return perms.length === 0 || perms.some(p => hasPermission(p, activeOrg?.code));
    })
  ), [user, effectiveUser, activeOrg, hasPermission]);

  // Fournie à GlobalSearch : liste plate des menus correspondant à la requête,
  // déjà filtrée par org / rôle / permission. La logique de droits reste ICI
  // (réutilisation des deux prédicats existants — aucune duplication).
  const searchMenuItems = React.useCallback(
    (rawQuery) => flattenMenuResults(orderedCategories, rawQuery, {
      customLabels,
      isCategoryAllowed: isCategoryAllowedForOrg,
      filterItems: filterItemsByRoleAndPerms,
    }),
    [orderedCategories, customLabels, isCategoryAllowedForOrg, filterItemsByRoleAndPerms],
  );

  // Section dont un item correspond à la route courante (pour l'épingle).
  const activeSection = React.useMemo(() => {
    const candidates = orderedCategories.filter(cat => {
      if (!isCategoryAllowedForOrg(cat)) return false;
      return filterItemsByRoleAndPerms(cat.items).some(item =>
        location.pathname === item.path ||
        location.pathname.startsWith(item.path + '/')
      );
    });
    if (candidates.length === 0) return null;
    // Préférer la section avec un item match exact (évite les collisions de préfixe
    // — ex. /parc/toners/facturation appartient à COMPTABILITÉ et non à PARC).
    const exact = candidates.find(cat =>
      filterItemsByRoleAndPerms(cat.items).some(item => item.path === location.pathname)
    );
    return exact || candidates[0];
  }, [orderedCategories, location.pathname, isCategoryAllowedForOrg, filterItemsByRoleAndPerms]);

  // Items visibles de la section active (filtrage rôle + permissions, identique à la boucle principale).
  const activeSectionVisibleItems = React.useMemo(() => {
    if (!activeSection) return [];
    return filterItemsByRoleAndPerms(activeSection.items);
  }, [activeSection, filterItemsByRoleAndPerms]);

  const [openCats, setOpenCats] = useState(() => {
    const init = {};
    sidebarCategories.forEach(cat => {
      const hasActive = cat.items.some(i => location.pathname === i.path || location.pathname.startsWith(i.path + '/'));
      init[cat.id] = cat.defaultOpen || hasActive;
    });
    return init;
  });

  const toggleCat = useCallback((catId) => {
    setOpenCats(prev => ({ ...prev, [catId]: !prev[catId] }));
  }, []);

  // Drag state for category reorder
  const dragCat = useRef(null);
  const [dragOverCat, setDragOverCat] = useState(null);

  const handleCatDragStart = (e, catId) => {
    dragCat.current = catId;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', catId);
  };
  const handleCatDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
  const handleCatDragEnter = (catId) => { if (dragCat.current && dragCat.current !== catId) setDragOverCat(catId); };
  const handleCatDrop = (e, targetCatId) => {
    e.preventDefault();
    if (!dragCat.current || dragCat.current === targetCatId) { dragCat.current = null; setDragOverCat(null); return; }
    const currentOrder = orderedCategories.map(c => c.id);
    const fromIdx = currentOrder.indexOf(dragCat.current);
    const toIdx = currentOrder.indexOf(targetCatId);
    if (fromIdx !== -1 && toIdx !== -1) {
      const newOrder = [...currentOrder];
      const [moved] = newOrder.splice(fromIdx, 1);
      newOrder.splice(toIdx, 0, moved);
      setSidebarOrder(newOrder);
      // Drag manuel = ordre custom voulu : on désactive le tri alpha sections
      // sinon il écrase immédiatement le drag à la prochaine recomposition.
      if (sidebarAlphaSortSections) setSidebarAlphaSortSections(false);
    }
    dragCat.current = null;
    setDragOverCat(null);
  };
  const handleCatDragEnd = () => { dragCat.current = null; setDragOverCat(null); };

  async function handleLogout() {
    await logout();
    navigate('/');
  }

  return (
    <aside
      className={`sidebar${mobileOpen ? ' sidebar-mobile-open' : ''}${!open ? ' sidebar-collapsed' : ''}`}
      data-testid="sidebar"
      style={{
        width: effectiveWidth,
        position: 'fixed', top: 0, left: 0, height: '100vh',
        background: 'linear-gradient(180deg, #1e4264 0%, #152d44 100%)',
        color: '#fff',
        transition: resizing.current ? 'none' : 'width var(--duration-normal) var(--ease-out), transform 0.2s ease',
        zIndex: 50,
        display: 'flex', flexDirection: 'column',
        // overflowY: auto plutôt que hidden — sinon, quand l'utilisateur agrandit
        // l'interface (`ui_scale` > 1) via /user/preferences, la sidebar zoomée
        // dépasse 100vh et le footer (avec le bouton "Préférences" lui-même !)
        // sort de l'écran sans recours possible. Cul-de-sac UX, fix critique.
        overflowY: 'auto', overflowX: 'hidden',
        borderRight: '1px solid rgba(255,255,255,0.04)',
      }}
    >
      {/* Resize handle */}
      {!collapsed && (
        <div
          onMouseDown={handleResizeStart}
          onTouchStart={handleResizeStart}
          style={{
            position: 'absolute', top: 0, right: 0, width: 5, height: '100%',
            cursor: 'col-resize', zIndex: 60,
            background: 'transparent',
          }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(91,163,230,0.4)'}
          onMouseLeave={e => { if (!resizing.current) e.currentTarget.style.background = 'transparent'; }}
        />
      )}
      {/* Logo */}
      <div style={{
        padding: collapsed ? '12px 0' : '16px',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'flex-start',
        gap: 10, minHeight: 64,
      }}>
        {/* Le logo ramène à l'ACCUEIL du compte affiché (`defaultPath`) : l'accueil de pilotage pour le dirigeant,
            la page d'arrivée habituelle pour les autres (William, 20/10/2026). */}
        <button type="button" aria-label="Accueil" title="Accueil" onClick={() => navigate(defaultPath || '/')}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', flexShrink: 0 }}>
          <img src={LOGO_SRC} alt="METERIS" style={{ height: 32, flexShrink: 0, background: '#fff', borderRadius: 6, padding: 2 }} />
        </button>
        {!collapsed && (
          <span className="sidebar-label" style={{ display: 'contents' }}>
            <span style={{
              fontFamily: "'IBM Plex Mono', monospace", fontWeight: 700,
              fontSize: 13, letterSpacing: 1.2, whiteSpace: 'nowrap', flex: 1,
              background: 'linear-gradient(135deg, #fff 0%, rgba(255,255,255,0.7) 100%)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }} title={activeOrg?.name || 'METERIS'}>
              {activeOrg?.code || activeOrg?.name || 'METERIS'}
            </span>
            <CartBellWidget />
            <NotificationBell />
          </span>
        )}
      </div>

      {/* Scope : Org (METERIS/MCF/SCI) puis Entité (client) */}
      {!collapsed && (
        <div style={{
          padding: '8px 12px',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          background: 'rgba(0,0,0,0.15)',
          display: 'flex', flexDirection: 'column', gap: 8,
        }}>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <OrgSelector compact darkTheme showAll />
          </div>
          <div data-tour="entity-selector" className="sidebar-entity"><EntitySelector /></div>
        </div>
      )}

      {/* Recherche unifiée (menus + tickets + utilisateurs + sociétés) */}
      {!collapsed && (
        <div className="sidebar-search" style={{
          padding: '10px 12px',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
        }}>
          <div data-tour="global-search"><GlobalSearch searchMenuItems={searchMenuItems} /></div>
        </div>
      )}

      {/* Nav */}
      <nav style={{ flex: 1, overflowY: 'auto', padding: '6px 0' }}>
        {/* Section Récents */}
        {!collapsed && recentPages.length > 0 && (
          <div style={{ marginBottom: 2 }}>
            <div style={{
              padding: '10px 16px 6px', fontSize: 11, fontWeight: 700, letterSpacing: 1.5,
              color: 'rgba(255,255,255,0.5)', fontFamily: "'IBM Plex Mono', monospace",
            }}>
              RECENTS
            </div>
            {recentPages.map(rp => {
              const item = allItemsByPath[rp.path];
              const Icon = item?.icon || Clock;
              const isActive = location.pathname === rp.path;
              return (
                <NavLink
                  key={rp.path}
                  to={rp.path}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '8px 16px 8px 28px',
                    color: isActive ? '#fff' : 'rgba(255,255,255,0.85)',
                    background: isActive ? 'rgba(255,255,255,0.1)' : 'transparent',
                    borderLeft: isActive ? '3px solid #5ba3e6' : '3px solid transparent',
                    textDecoration: 'none', fontSize: 14, fontWeight: isActive ? 600 : 400,
                    transition: 'all var(--duration-fast) var(--ease-out)', whiteSpace: 'nowrap',
                  }}
                >
                  <Icon size={16} style={{ flexShrink: 0 }} />
                  <span className="sidebar-label">{rp.label}</span>
                </NavLink>
              );
            })}
            <div style={{ height: 1, background: 'rgba(255,255,255,0.06)', margin: '6px 16px' }} />
          </div>
        )}

        {/* Section épinglée : section contenant l'item actif (mode normal) */}
        {!collapsed && activeSection && activeSectionVisibleItems.length > 0 && (
          <div style={{ marginBottom: 2 }} data-testid={`sidebar-pinned-${activeSection.id}`}>
            <div style={{
              display: 'flex', alignItems: 'center', width: '100%',
              padding: '10px 16px 6px',
              color: 'rgba(255,255,255,0.75)',
              fontSize: 11, fontWeight: 700, letterSpacing: 1.5,
              fontFamily: "'IBM Plex Mono', monospace",
              textAlign: 'left', gap: 4,
            }}>
              <ChevronDown size={12} style={{ flexShrink: 0 }} />
              <span className="sidebar-cat-label">
                {customLabels?.[`cat:${activeSection.id}`] || activeSection.label}
              </span>
            </div>
            {activeSectionVisibleItems.map(item => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={`pinned-${item.path}`}
                  to={item.path}
                  style={({ isActive }) => ({
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '8px 16px 8px 28px',
                    justifyContent: 'flex-start',
                    color: isActive ? '#fff' : 'rgba(255,255,255,0.85)',
                    background: isActive ? 'rgba(255,255,255,0.1)' : 'transparent',
                    borderLeft: isActive ? '3px solid #5ba3e6' : '3px solid transparent',
                    textDecoration: 'none', fontSize: 14,
                    fontWeight: isActive ? 600 : 400,
                    transition: 'all var(--duration-fast) var(--ease-out)',
                    whiteSpace: 'nowrap',
                    position: 'relative',
                  })}
                >
                  <Icon size={16} style={{ flexShrink: 0 }} />
                  <span className="sidebar-label">{customLabels?.[item.path] || item.label}</span>
                  {item.badge && BADGE_COUNTS[item.badge] > 0 && (
                    <span style={{
                      marginLeft: 'auto',
                      background: '#dc2626', color: 'white',
                      fontSize: 10, fontWeight: 700,
                      padding: '1px 6px', borderRadius: 10,
                      minWidth: 18, textAlign: 'center',
                    }}>{BADGE_COUNTS[item.badge]}</span>
                  )}
                </NavLink>
              );
            })}
            <div style={{ height: 1, background: 'rgba(255,255,255,0.06)', margin: '6px 16px' }} />
          </div>
        )}

        {/* Section épinglée en mode collapsed : icône en tête */}
        {collapsed && activeSection && activeSectionVisibleItems.length > 0 && (
          <div data-testid={`sidebar-pinned-collapsed-${activeSection.id}`}>
            <CollapsedCategory
              cat={activeSection}
              visibleItems={activeSectionVisibleItems}
              hasActive={true}
              customLabels={customLabels}
              location={location}
            />
            <div style={{ height: 1, background: 'rgba(255,255,255,0.06)', margin: '6px 8px' }} />
          </div>
        )}

        {orderedCategories.map(cat => {
          // Filtre par organisation interne active : masque les sections non pertinentes
          // (ex: Parc/Proxmox/Docker sur MCF, formation sur METERIS)
          // Superadmin bypass : voit toutes les sections quelle que soit l'org active
          if (!isCategoryAllowedForOrg(cat)) return null;
          // Resout via roles_by_org pour l'org active (ex: Ahmed=tech_n2 sur METERIS,
          // mcf_commercial sur MCF -> sections MCF apparaissent quand MCF est actif).
          const visibleItems = filterItemsByRoleAndPerms(cat.items);
          if (visibleItems.length === 0) return null;

          const isOpen = openCats[cat.id];
          const hasActive = visibleItems.some(i => location.pathname === i.path || location.pathname.startsWith(i.path + '/'));
          const isDragOver = dragOverCat === cat.id;

          return (
            <div
              key={cat.id}
              data-testid={`sidebar-cat-${cat.id}`}
              style={{ marginBottom: 2 }}
            >
              {/* Category header + items */}
              {collapsed ? (
                /* ── Collapsed: one icon per section with flyout ── */
                <CollapsedCategory
                  cat={cat}
                  visibleItems={visibleItems}
                  hasActive={hasActive}
                  customLabels={customLabels}
                  location={location}
                />
              ) : (
                <>
                  <button
                    onClick={() => toggleCat(cat.id)}
                    data-testid={`sidebar-toggle-${cat.id}`}
                    style={{
                      display: 'flex', alignItems: 'center', width: '100%',
                      padding: '10px 16px 6px', border: 'none', background: 'none',
                      cursor: 'pointer',
                      color: hasActive ? 'rgba(255,255,255,0.75)' : 'rgba(255,255,255,0.5)',
                      fontSize: 11, fontWeight: 700, letterSpacing: 1.5,
                      fontFamily: "'IBM Plex Mono', monospace",
                      textAlign: 'left', gap: 4,
                      transition: 'color var(--duration-fast) var(--ease-out)',
                    }}
                  >
                    <ChevronDown
                      size={12}
                      className="sidebar-cat-label"
                      style={{
                        transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                        transition: 'transform var(--duration-normal) var(--ease-out)',
                        flexShrink: 0,
                      }}
                    />
                    <span className="sidebar-cat-label">{customLabels?.[`cat:${cat.id}`] || cat.label}</span>
                  </button>
                  {isOpen && visibleItems.map(item => {
                    const Icon = item.icon;
                    return (
                      <NavLink
                        key={item.path}
                        to={item.path}
                        data-testid={`nav-${item.path.replace(/\//g, '-').replace(/^-/, '')}`}
                        style={({ isActive }) => ({
                          display: 'flex', alignItems: 'center', gap: 10,
                          padding: '8px 16px 8px 28px',
                          justifyContent: 'flex-start',
                          color: isActive ? '#fff' : 'rgba(255,255,255,0.85)',
                          background: isActive ? 'rgba(255,255,255,0.1)' : 'transparent',
                          borderLeft: isActive ? '3px solid #5ba3e6' : '3px solid transparent',
                          textDecoration: 'none', fontSize: 14,
                          fontWeight: isActive ? 600 : 400,
                          transition: 'all var(--duration-fast) var(--ease-out)',
                          whiteSpace: 'nowrap', position: 'relative',
                        })}
                      >
                        <Icon size={16} style={{ flexShrink: 0 }} />
                        <span className="sidebar-label">{customLabels?.[item.path] || item.label}</span>
                        {item.badge && BADGE_COUNTS[item.badge] > 0 && (
                          <span style={{
                            marginLeft: 'auto',
                            background: '#dc2626', color: 'white',
                            fontSize: 10, fontWeight: 700,
                            padding: '1px 6px', borderRadius: 10,
                            minWidth: 18, textAlign: 'center',
                          }}>{BADGE_COUNTS[item.badge]}</span>
                        )}
                      </NavLink>
                    );
                  })}
                </>
              )}

              {/* Report sub-items (always visible when section open) */}
              {cat.id === 'rapports' && isOpen && !collapsed && (
                <div style={{ marginLeft: 24 }}>
                  {reportSubItems.map(item => {
                    const Icon = item.icon;
                    const isActive = location.pathname.includes(item.path);
                    return (
                      <div key={item.path} style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '6px 16px', fontSize: 12,
                        color: isActive ? '#5ba3e6' : 'rgba(255,255,255,0.45)',
                        fontWeight: isActive ? 600 : 400,
                        borderLeft: '1px solid rgba(255,255,255,0.08)',
                        transition: 'color var(--duration-fast) var(--ease-out)',
                      }}>
                        <Icon size={14} />
                        {item.label}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* User + Logout */}
      <div style={{
        padding: collapsed ? '8px 0' : '12px 16px',
        borderTop: '1px solid rgba(255,255,255,0.08)',
        display: 'flex', alignItems: 'center',
        flexDirection: collapsed ? 'column' : 'row',
        justifyContent: collapsed ? 'center' : 'flex-start',
        gap: collapsed ? 4 : 8, minHeight: collapsed ? 'auto' : 52,
        background: 'rgba(0,0,0,0.1)',
      }}>
        {!collapsed && user && (
          <div className="sidebar-user-info" style={{ flex: 1, overflow: 'hidden' }}>
            <div style={{
              fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.85)',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {user.name}
            </div>
            <div style={{
              fontSize: 10, color: 'rgba(255,255,255,0.35)',
              letterSpacing: 0.5, fontFamily: "'IBM Plex Mono', monospace",
            }}>
              {user.role_label || 'Technicien'}
            </div>
          </div>
        )}
        <button
          data-testid="user-preferences-button"
          onClick={() => navigate('/user/preferences')}
          title="Mes preferences"
          style={{
            background: 'none', border: '1px solid transparent', color: 'rgba(255,255,255,0.35)',
            cursor: 'pointer', padding: 6, borderRadius: 6,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'all var(--duration-fast) var(--ease-out)', flexShrink: 0,
          }}
          onMouseEnter={e => {
            e.currentTarget.style.color = '#5ba3e6';
            e.currentTarget.style.background = 'rgba(91,163,230,0.12)';
            e.currentTarget.style.borderColor = 'rgba(91,163,230,0.2)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.color = 'rgba(255,255,255,0.35)';
            e.currentTarget.style.background = 'none';
            e.currentTarget.style.borderColor = 'transparent';
          }}
        >
          <SlidersHorizontal size={16} />
        </button>
        <button
          data-testid="logout-button"
          onClick={handleLogout}
          title="Deconnexion"
          style={{
            background: 'none', border: '1px solid transparent', color: 'rgba(255,255,255,0.35)',
            cursor: 'pointer', padding: 6, borderRadius: 6,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'all var(--duration-fast) var(--ease-out)', flexShrink: 0,
          }}
          onMouseEnter={e => {
            e.currentTarget.style.color = '#ff6b6b';
            e.currentTarget.style.background = 'rgba(255,60,60,0.12)';
            e.currentTarget.style.borderColor = 'rgba(255,60,60,0.2)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.color = 'rgba(255,255,255,0.35)';
            e.currentTarget.style.background = 'none';
            e.currentTarget.style.borderColor = 'transparent';
          }}
        >
          <LogOut size={16} />
        </button>
      </div>

      {/* Toggle */}
      <button
        onClick={onToggle}
        data-testid="sidebar-toggle"
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 12, background: 'rgba(255,255,255,0.03)',
          border: 'none', borderTop: '1px solid rgba(255,255,255,0.06)',
          color: 'rgba(255,255,255,0.5)', cursor: 'pointer',
          transition: 'all var(--duration-fast) var(--ease-out)',
        }}
      >
        {open ? <ChevronLeft size={18} /> : <Menu size={18} />}
      </button>
    </aside>
  );
}
